import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import {
  DashboardComponentAlign,
  DashboardComponentGroup,
  DashboardComponentJustify,
} from "api/model/dashboard/DashboardComponentGroup";
import { Button } from "primereact/button";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import useDashboardStore from "store/dashboard-store";
import { ProgressSpinner } from "primereact/progressspinner";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { getDefaultLayout } from "../layouts/helpers";
import { tabUpdateInDashboard, updateDashboardTabs } from "../../utils";

interface GroupConfiguratorProps {
  tabId: number;
  group: DashboardComponentGroup;
  isDialogVisible: boolean;
  setIsDialogVisible: (isDialogVisible: boolean) => void;
  isComponentsReorderEnabled: boolean;
  setIsComponentsReorderEnabled: (isComponentReorderEnabled: boolean) => void;
  setMoveTabGroupDialogVisibility: (moveTabGroupDialogVisibility: boolean) => void;
  setDashboardComponentCreationDialogVisibility: (
    dashboardComponentCreationDialogVisibility: boolean
  ) => void;
}

const layoutOptions = [
  { label: T("layout.vertical"), value: DashboardLayoutType.VERTICAL },
  { label: T("layout.horizontal-flex"), value: DashboardLayoutType.HORIZONTAL_FLEX },
  { label: T("layout.grid"), value: DashboardLayoutType.GRID },
];

const justifyOptions = [
  { label: T("layout.start"), value: DashboardComponentJustify.START },
  { label: T("layout.center"), value: DashboardComponentJustify.CENTER },
  { label: T("layout.end"), value: DashboardComponentJustify.END },
  { label: T("layout.space-between"), value: DashboardComponentJustify.BETWEEN },
  { label: T("layout.space-around"), value: DashboardComponentJustify.AROUND },
  { label: T("layout.space-evenly"), value: DashboardComponentJustify.EVENLY },
];

const alignOptions = [
  { label: T("layout.start"), value: DashboardComponentAlign.START },
  { label: T("layout.center"), value: DashboardComponentAlign.CENTER },
  { label: T("layout.end"), value: DashboardComponentAlign.END },
  { label: T("layout.stretch"), value: DashboardComponentAlign.STRETCH },
];

const GroupConfigurator = ({
  tabId,
  group,
  isDialogVisible,
  isComponentsReorderEnabled,
  setIsDialogVisible,
  setIsComponentsReorderEnabled,
  setMoveTabGroupDialogVisibility,
  setDashboardComponentCreationDialogVisibility,
}: GroupConfiguratorProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const [localGroup, setLocalGroup] = useState<DashboardComponentGroup>({ ...group });
  const [newGroupTitle, setNewGroupTitle] = useState<string>(group.title || "");
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);

  const handleReorderComponents = () => {
    setIsGroupReorderEnabled(false);
    setIsComponentsReorderEnabled(!isComponentsReorderEnabled);
  };

  if (!dashboard) {
    return <ProgressSpinner />;
  }

  const handleInputChange = (
    field: string,
    value: DashboardComponentAlign | DashboardComponentJustify | DashboardLayoutType
  ) => {
    setLocalGroup(prevGroup => ({
      ...prevGroup,
      layout: {
        ...prevGroup.layout,
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    updateDashboardTabs(
      dashboard,
      dashboard.dashboardTabsJs?.map(tab => {
        return {
          ...tab,
          componentGroups: tab.componentGroups.map(g => {
            if (g.id === localGroup.id) {
              const layoutTypeChanged = g.layout.type !== localGroup.layout.type;
              const updatedComponents = g.components.map(component => {
                if (layoutTypeChanged) {
                  return {
                    ...component,
                    title: newGroupTitle,
                    layout: getDefaultLayout(localGroup.layout.type),
                  };
                }
                return component;
              });
              return {
                ...g,
                title: newGroupTitle,
                components: updatedComponents,
                layout: localGroup.layout,
              };
            }
            return g;
          }),
        };
      })
    );
    setIsDialogVisible(false);
  };

  const groupRemoveConfirm = (id: string) => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("confirm-dialog.remove-group"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-group"),
      confirmedCallback: () => {
        handleRemoveGroup(id);
      },
    });
  };

  const handleRemoveGroup = (id: string) => {
    if (!dashboard) return;

    const currentTabIndex = tabId;
    const updatedComponentGroups = dashboard.dashboardTabsJs[
      currentTabIndex
    ].componentGroups.filter((groupToFilter: DashboardComponentGroup) => groupToFilter.id !== id);

    const updatedDashboardTabs = tabUpdateInDashboard(dashboard.dashboardTabsJs, currentTabIndex, {
      componentGroups: updatedComponentGroups,
    });

    updateDashboardTabs(dashboard, updatedDashboardTabs);
  };

  const handleConfiguratorFooter = () => {
    return (
      <>
        <Button
          tooltip={T("word.cancel")}
          label={T("word.cancel")}
          icon="pi pi-times"
          className="component-editor-button icon-red"
          onClick={() => setIsDialogVisible(false)}
        />
        <Button
          tooltip={T("action.save")}
          label={T("action.save")}
          icon="pi pi-check"
          className="component-editor-button icon-green"
          onClick={handleSave}
        />
      </>
    );
  };

  const renderInputField = (
    id: string,
    label: string,
    value: any,
    options: any[] | undefined,
    onChange: (e: any) => void,
    inputType: "dropdown" | "inputnumber",
    disabled: boolean
  ) => {
    return (
      <div className="w-full gap-2 flex flex-column align-items-left">
        <label htmlFor={id}>{label}</label>
        {inputType === "dropdown" ? (
          <Dropdown
            id={id}
            className="w-full"
            value={value}
            options={options}
            onChange={onChange}
            disabled={disabled}
          />
        ) : (
          <InputNumber
            id={id}
            className="w-full"
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  return (
    <Dialog
      header={T("label.group-configurator")}
      visible={isDialogVisible}
      onHide={() => setIsDialogVisible(false)}
      draggable={false}
      blockScroll
      style={{ width: "500px" }}
      footer={handleConfiguratorFooter}
    >
      <div className="flex flex-column gap-4">
        <div className="w-full">
          <div>{T("label.group-title")}</div>
          <InputText
            value={newGroupTitle}
            className="w-full"
            onChange={e => setNewGroupTitle(e.target.value)}
          />
        </div>
        <div className="w-full flex flex-row gap-3">
          <Button
            icon="pi pi-plus"
            label={T("action.add")}
            tooltip={T("action.add-component")}
            className="component-editor-button icon-green"
            onClick={() => setDashboardComponentCreationDialogVisibility(true)}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-arrow-right-arrow-left"
            label={T("action.reorder")}
            tooltip={T("action.reorder-components")}
            className="component-editor-button"
            onClick={handleReorderComponents}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-arrow-right"
            label={T("action.move")}
            tooltip={T("action.move-group")}
            className="component-editor-button"
            onClick={() => setMoveTabGroupDialogVisibility(true)}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-trash"
            label={T("action.remove")}
            tooltip={T("confirm-dialog.remove-group")}
            className="component-editor-button icon-red"
            onClick={() => groupRemoveConfirm(group.id)}
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
        <div className="w-full">
          {renderInputField(
            "type",
            T("label.layout-type"),
            localGroup.layout.type,
            layoutOptions,
            e => handleInputChange("type", e.value),
            "dropdown",
            false
          )}
        </div>
        {localGroup.layout.type !== DashboardLayoutType.GRID && (
          <div className="flex flex-row gap-4 justify-content-between">
            <div className="flex flex-column gap-4">
              {renderInputField(
                "align",
                T("label.vertical-align"),
                localGroup.layout.align,
                alignOptions,
                e => handleInputChange("align", e.value),
                "dropdown",
                false
              )}
            </div>
            <div className="flex flex-column gap-4">
              {renderInputField(
                "justify",
                T("label.horizontal-align"),
                localGroup.layout.justify,
                justifyOptions,
                e => handleInputChange("justify", e.value),
                "dropdown",
                localGroup.layout.type === DashboardLayoutType.VERTICAL
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default GroupConfigurator;
